import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import FormField from './FormField';
import CheckboxField from './CheckboxField';
import SuccessMessage from './SuccessMessage';

const CustomerDataForm = () => {
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    firstName: '',
    lastName: '',
    companyName: '',
    vatId: '',
    billingSameAsPrivate: true,
    billingStreet: '',
    billingZip: '',
    billingCity: '',
    billingCountry: 'Deutschland',
    invoiceEmail: '',
    notes: '',
    generalMessage: '',
    updateReason: 'first_registration',
    privacyAccepted: false,
    termsAccepted: false,
    revocationAcknowledged: false,
    marketingOptIn: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = 'E-Mail-Adresse ist erforderlich';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
    }

    // Phone validation
    const phoneRegex = /^\+?[\d\s\-()]{6,}$/;
    if (!formData.phone) {
      newErrors.phone = 'Telefonnummer ist erforderlich';
    } else if (!phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Bitte geben Sie eine gültige Telefonnummer ein (mind. 6 Ziffern)';
    }

    // Required fields
    if (!formData.firstName) newErrors.firstName = 'Vorname ist erforderlich';
    if (!formData.lastName) newErrors.lastName = 'Nachname ist erforderlich';

    // Billing address validation if different from private
    if (!formData.billingSameAsPrivate) {
      if (!formData.billingStreet) newErrors.billingStreet = 'Straße & Hausnummer ist erforderlich';
      if (!formData.billingZip) newErrors.billingZip = 'PLZ ist erforderlich';
      if (!formData.billingCity) newErrors.billingCity = 'Ort ist erforderlich';
      if (!formData.billingCountry) newErrors.billingCountry = 'Land ist erforderlich';
    }

    // Required consents
    if (!formData.privacyAccepted) newErrors.privacyAccepted = 'Datenschutzerklärung muss akzeptiert werden';
    if (!formData.termsAccepted) newErrors.termsAccepted = 'AGB müssen akzeptiert werden';
    if (!formData.revocationAcknowledged) newErrors.revocationAcknowledged = 'Widerrufsbelehrung muss zur Kenntnis genommen werden';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const webhookData = {
        source: "managershop.com",
        timestamp: new Date().toISOString(),
        action: "customer_data_update",
        customer: {
          email: formData.email,
          phone: formData.phone,
          first_name: formData.firstName,
          last_name: formData.lastName,
          company_name: formData.companyName || null,
          vat_id: formData.vatId || null,
          billing_same_as_private: formData.billingSameAsPrivate,
          billing_address: formData.billingSameAsPrivate ? null : {
            street: formData.billingStreet,
            zip: formData.billingZip,
            city: formData.billingCity,
            country: formData.billingCountry
          },
          invoice_email: formData.invoiceEmail || null,
          notes: formData.notes || null,
          general_message: formData.generalMessage || null
        },
        update_reason: formData.updateReason,
        consents: {
          privacy_policy_acceptance: formData.privacyAccepted,
          terms_acceptance: formData.termsAccepted,
          revocation_notice_ack: formData.revocationAcknowledged,
          marketing_email_opt_in: formData.marketingOptIn
        }
      };

      const webhookUrl = 'https://hook.integrator.boost.space/30ekpgob2c55nvc5m0wvugnpi2rwvglp';
      
      // Use fetch to send data to the webhook
      await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookData)
      });
      
      // Reset form and show success
      setFormData({
        email: '',
        phone: '',
        firstName: '',
        lastName: '',
        companyName: '',
        vatId: '',
        billingSameAsPrivate: true,
        billingStreet: '',
        billingZip: '',
        billingCity: '',
        billingCountry: 'Deutschland',
        invoiceEmail: '',
        notes: '',
        generalMessage: '',
        updateReason: 'first_registration',
        privacyAccepted: false,
        termsAccepted: false,
        revocationAcknowledged: false,
        marketingOptIn: false
      });
      
      setIsSubmitted(true);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  if (isSubmitted) {
    return <SuccessMessage onReset={() => setIsSubmitted(false)} />;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Persönliche Daten */}
      <div className="border-b border-gray-200 pb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <span className="bg-blue-100 text-blue-600 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-medium">
            1
          </span>
          Persönliche Daten
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Vorname"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            error={errors.firstName}
            required
          />
          <FormField
            label="Nachname"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            error={errors.lastName}
            required
          />
        </div>
      </div>

      {/* Erreichbarkeit */}
      <div className="border-b border-gray-200 pb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <span className="bg-blue-100 text-blue-600 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-medium">
            2
          </span>
          Erreichbarkeit
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="E-Mail-Adresse"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            error={errors.email}
            required
          />
          <FormField
            label="Telefonnummer"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleInputChange}
            error={errors.phone}
            required
          />
        </div>
      </div>

      {/* Optionale Unternehmensdaten */}
      <div className="border-b border-gray-200 pb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Optionale Unternehmensdaten</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Firmenname / Organisation"
            name="companyName"
            value={formData.companyName}
            onChange={handleInputChange}
          />
          <FormField
            label="Umsatzsteuer-ID"
            name="vatId"
            value={formData.vatId}
            onChange={handleInputChange}
          />
        </div>
      </div>

      {/* Rechnungsanschrift */}
      <div className="border-b border-gray-200 pb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Rechnungsanschrift</h2>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Ist Ihre Rechnungsanschrift identisch mit Ihrer persönlichen Anschrift? *
          </label>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="billingSameAsPrivate"
                checked={formData.billingSameAsPrivate === true}
                onChange={() => handleInputChange('billingSameAsPrivate', true)}
                className="mr-2"
              />
              Ja
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="billingSameAsPrivate"
                checked={formData.billingSameAsPrivate === false}
                onChange={() => handleInputChange('billingSameAsPrivate', false)}
                className="mr-2"
              />
              Nein
            </label>
          </div>
        </div>

        {!formData.billingSameAsPrivate && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              label="Rechnung – Straße & Hausnummer"
              name="billingStreet"
              value={formData.billingStreet}
              onChange={handleInputChange}
              error={errors.billingStreet}
              required
            />
            <FormField
              label="Rechnung – PLZ"
              name="billingZip"
              value={formData.billingZip}
              onChange={handleInputChange}
              error={errors.billingZip}
              required
            />
            <FormField
              label="Rechnung – Ort"
              name="billingCity"
              value={formData.billingCity}
              onChange={handleInputChange}
              error={errors.billingCity}
              required
            />
            <FormField
              label="Rechnung – Land"
              name="billingCountry"
              value={formData.billingCountry}
              onChange={handleInputChange}
              error={errors.billingCountry}
              required
            />
          </div>
        )}
      </div>

      {/* Optionale Zusatzinformationen */}
      <div className="border-b border-gray-200 pb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Optionale Zusatzinformationen</h2>
        <div className="space-y-4">
          <FormField
            label="Abweichende Rechnungs-E-Mail"
            name="invoiceEmail"
            type="email"
            value={formData.invoiceEmail}
            onChange={handleInputChange}
          />
          <FormField
            label="Hinweise für die Rechnungsstellung"
            name="notes"
            type="textarea"
            value={formData.notes}
            onChange={handleInputChange}
            rows={2}
          />
          <FormField
            label="Allgemeine Nachricht / Sonstiges"
            name="generalMessage"
            type="textarea"
            value={formData.generalMessage}
            onChange={handleInputChange}
            rows={2}
          />
        </div>
      </div>

      {/* Änderungstyp */}
      <div className="border-b border-gray-200 pb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Änderungstyp</h2>
        <FormField
          label="Grund der Datenaktualisierung"
          name="updateReason"
          type="select"
          value={formData.updateReason}
          onChange={handleInputChange}
          options={[
            { value: 'first_registration', label: 'Erstmalige Registrierung' },
            { value: 'address_change', label: 'Adressänderung' },
            { value: 'legal_form_change', label: 'Rechtsformänderung (z. B. GbR → GmbH)' },
            { value: 'name_change', label: 'Namensänderung' },
            { value: 'other', label: 'Sonstiges' }
          ]}
          required
        />
      </div>

      {/* Rechtliche Zustimmungen */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Rechtliche Zustimmungen</h2>
        
        <CheckboxField
          name="privacyAccepted"
          checked={formData.privacyAccepted}
          onChange={handleInputChange}
          error={errors.privacyAccepted}
          required
        >
          Ich habe die{' '}
          <Link
            to="/datenschutz"
            className="text-blue-600 hover:text-blue-800 underline"
          >
            Datenschutzerklärung
          </Link>{' '}
          gelesen und akzeptiere sie.
        </CheckboxField>

        <CheckboxField
          name="termsAccepted"
          checked={formData.termsAccepted}
          onChange={handleInputChange}
          error={errors.termsAccepted}
          required
        >
          Ich habe die{' '}
          <Link
            to="/agb"
            className="text-blue-600 hover:text-blue-800 underline"
          >
            Allgemeinen Geschäftsbedingungen
          </Link>{' '}
          gelesen und akzeptiere sie.
        </CheckboxField>

        <CheckboxField
          name="revocationAcknowledged"
          checked={formData.revocationAcknowledged}
          onChange={handleInputChange}
          error={errors.revocationAcknowledged}
          required
        >
          Ich habe die{' '}
          <Link
            to="/widerruf"
            className="text-blue-600 hover:text-blue-800 underline"
          >
            Widerrufsbelehrung
          </Link>{' '}
          gelesen und zur Kenntnis genommen.
        </CheckboxField>

        <CheckboxField
          name="marketingOptIn"
          checked={formData.marketingOptIn}
          onChange={handleInputChange}
        >
          Ich möchte gelegentlich per E-Mail über relevante Angebote informiert werden.
        </CheckboxField>
      </div>

      {/* Submit Button */}
      <div className="pt-6">
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? 'Wird übermittelt...' : 'Daten übermitteln'}
        </button>
      </div>
    </form>
  );
};

export default CustomerDataForm;